import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronRight } from "lucide-react";
import Reveal from "./Reveal";
import { specMove } from "./glass";

const StageVisual = ({ id }) => {
  switch (id) {
    case "idea":
      return (
        <div className="space-y-2.5">
          <div className="h-1.5 w-4/5 rounded-full bg-[#3b82f6]" />
          <div className="h-1.5 w-3/5 rounded-full bg-white/35" />
          <div className="h-1.5 w-2/5 rounded-full bg-white/15" />
        </div>
      );
    case "title":
      return (
        <div className="space-y-2.5">
          <div className="h-2.5 w-full rounded-full bg-white/85" />
          <div className="h-1.5 w-1/2 rounded-full bg-white/25" />
        </div>
      );
    case "thumbnail":
      return (
        <div className="relative flex aspect-[16/9] items-center justify-center rounded-lg border border-white/15 bg-gradient-to-br from-white/[0.14] to-white/[0.03]">
          <div className="ml-0.5 h-0 w-0 border-y-[7px] border-l-[11px] border-y-transparent border-l-white/90" />
          <span className="absolute bottom-1.5 right-1.5 h-1 w-7 rounded-full bg-white/25" />
        </div>
      );
    case "video":
      return (
        <div className="space-y-2.5">
          <div className="flex h-7 items-stretch gap-1 rounded-md bg-white/[0.06] p-1">
            <div className="w-1/3 rounded-[4px] bg-white/70" />
            <div className="w-1/4 rounded-[4px] bg-white/30" />
            <div className="flex-1 rounded-[4px] bg-white/10" />
          </div>
          <div className="relative h-1 rounded-full bg-white/10">
            <div className="absolute left-1/3 -top-[3px] h-2.5 w-px bg-white/80" />
          </div>
        </div>
      );
    case "result":
      return (
        <svg viewBox="0 0 120 48" className="w-full" fill="none">
          <path
            d="M4 42 C 24 40, 34 30, 50 32 S 78 20, 92 17 S 112 7, 118 5 L118 48 L4 48 Z"
            fill="rgba(22,163,74,0.14)"
          />
          <path
            d="M4 42 C 24 40, 34 30, 50 32 S 78 20, 92 17 S 112 7, 118 5"
            stroke="#22c55e"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return null;
  }
};

const STAGES = [
  { id: "idea", label: "Attractive Ideas" },
  { id: "title", label: "SEO" },
  { id: "thumbnail", label: "Testing Thumbnails" },
  { id: "video", label: "Editing" },
  { id: "result", label: "Result" },
];

const TiltBlock = ({ index, stage }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 140, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 140, damping: 14 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      className="group relative"
      style={{ perspective: 700 }}
      data-testid={`stage-block-${stage.id}`}
      tabIndex={0}
    >
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        className="relative"
      >
        <div
          className="absolute inset-0 rounded-[26px] border border-white/[0.07] bg-black"
          style={{ transform: "translateZ(-24px) translate(7px, 9px)" }}
          aria-hidden="true"
        />
        <div className="relative rounded-[26px] border border-white/10 bg-[#121212] p-6 min-h-[190px] flex flex-col justify-between transition-all duration-500 group-hover:border-white/35 group-hover:bg-[#161616] group-hover:shadow-[0_0_70px_rgba(255,255,255,0.13),0_34px_70px_rgba(0,0,0,0.6)]">
          <div style={{ transform: "translateZ(30px)" }}>
            <div className="flex items-center justify-between mb-6">
              <span className="eyebrow text-[#6e6e73] transition-colors duration-300 group-hover:text-white/70">
                {stage.label}
              </span>
              <span className="text-xs font-medium text-white/25 transition-colors duration-300 group-hover:text-white/80">
                {String(index + 1).padStart(2, "0")}
              </span>
            </div>
            <div className="transition-transform duration-500 group-hover:scale-[1.06]">
              <StageVisual id={stage.id} />
            </div>
          </div>
        </div>
        {index < STAGES.length - 1 && (
          <ChevronRight
            className="hidden md:block absolute -right-[13px] top-1/2 -translate-y-1/2 h-4 w-4 text-white/30 z-10"
            aria-hidden="true"
          />
        )}
      </motion.div>
    </div>
  );
};

const LogoBlock = () => (
  <motion.div
    animate={{ y: [0, -10, 0] }}
    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    className="w-40 sm:w-56"
  >
    <img
      src="/vontrail-logo.png"
      alt="VONTRAIL logo"
      draggable={false}
      className="h-auto w-full select-none rounded-[2.5rem] shadow-[0_40px_90px_rgba(0,0,0,0.6)]"
      data-testid="chapter-01-logo"
    />
  </motion.div>
);

const Chapter01 = () => {
  return (
    <section className="bg-[#0a0a0a] py-28 sm:py-40" data-testid="chapter-01-alone">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <LogoBlock />
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7 lg:border-l lg:border-white/10 lg:pl-16">
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.06] text-white">
              If you're not getting above 50k views consistently, you're wasting your time and money.
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 text-lg sm:text-xl leading-relaxed max-w-xl">
            <p className="text-white font-medium">
              As a busy business owner, the last thing you want to do is squander your valuable time
              on YouTube channels that attract fewer than 30k views per video. Let the pros who do
              this for a living handle it for you and get you 10x more results while buying back your
              time.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Chapter02 = () => (
  <section className="bg-[#0a0a0a] border-t border-white/[0.08] py-28 sm:py-40" data-testid="chapter-02-strategy">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="max-w-2xl">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.06] text-white">
            Here's the deal: you invest in us monthly to outsource the best work and manage it. In
            return, we handle everything needed to grow your socials.
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="mt-8 space-y-6 text-[#a1a1a6] text-lg sm:text-xl leading-relaxed">
          <p>
            <span className="text-white font-medium">
              "You don't need to be famous to make money."
            </span>{" "}
            Sure. But if you can make{" "}
            <span className="text-white font-medium">$50K/month from 10K loyal followers</span>,
            imagine what happens when you have{" "}
            <span className="text-white font-medium">500K.</span> Why settle for less when the world
            is ready to give you more?
            <span className="mt-2 block text-sm text-[#6e6e73]">
              (This is for the people who never settle for less.)
            </span>
          </p>
          <p>
            We look at the idea, the audience, the message, the title, the thumbnail, and the video
            itself. <span className="text-white font-medium">Everything has to work together.</span>
          </p>
        </Reveal>
      </div>

      <div className="relative mt-20">
        <div className="hidden md:block absolute left-0 right-0 top-1/2 h-px bg-white/[0.07]" aria-hidden="true" />
        <div className="grid md:grid-cols-5 gap-5">
          {STAGES.map((s, i) => (
            <Reveal key={s.id} delay={0.1 + i * 0.12} y={36}>
              <TiltBlock index={i} stage={s} />
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default function Chapters() {
  return (
    <div id="manifesto">
      <Chapter01 />
      <Chapter02 />
    </div>
  );
}

const CrewVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 55, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 55, damping: 14 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    specMove(e);
  };

  return (
    <Reveal>
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => {
          mx.set(0);
          my.set(0);
        }}
        className="relative"
        style={{ perspective: 1000 }}
        data-testid="crew-visual"
      >
        <motion.div
          style={{ rotateX: rx, rotateY: ry }}
          animate={{ y: [0, -12, 0] }}
          whileHover={{ scale: 1.03, rotate: -0.5 }}
          transition={{
            y: { duration: 7, repeat: Infinity, ease: "easeInOut" },
            scale: { type: "spring", stiffness: 180, damping: 16 },
          }}
          className="group relative overflow-hidden rounded-[3rem] border border-black/[0.08] shadow-[0_50px_100px_rgba(0,0,0,0.22)]"
        >
          <img
            src="/crew-beach.jpeg"
            alt="The VONTRAIL crew handling filming, editing, and publishing while the client relaxes on the beach"
            className="block w-full h-auto select-none"
            loading="lazy"
            decoding="async"
            draggable={false}
          />
          <div
            className="pointer-events-none absolute inset-0 rounded-[3rem] shadow-[inset_0_1px_1px_rgba(255,255,255,0.55),inset_0_0_0_1px_rgba(255,255,255,0.12)]"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-0 rounded-[3rem] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            style={{
              background:
                "radial-gradient(420px circle at var(--gx, 50%) var(--gy, 50%), rgba(255,255,255,0.35), transparent 60%)",
            }}
            aria-hidden="true"
          />
        </motion.div>
      </div>
    </Reveal>
  );
};

export function Chapter04() {
  return (
    <section className="py-32 sm:py-44 bg-white" data-testid="chapter-04-ownership">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.06] text-[#1d1d1f]">
              We become your second brain.
            </h2>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 max-w-xl space-y-6 text-[#6e6e73] text-lg sm:text-xl leading-relaxed">
            <p>You already have enough to think about.</p>
            <div>
              <p>And we know you don't have time to:</p>
              <ul className="mt-4 space-y-2">
                {[
                  "Chase editors for revisions.",
                  "Find a great editor from millions.",
                  "Find a thumbnail designer whose thumbnails aren't just wallpapers.",
                  "Keep testing designers until one finally gets it right.",
                ].map((item) => (
                  <li
                    key={item}
                    className="group flex items-center gap-3.5 -mx-3 rounded-2xl px-3 py-2.5 cursor-default transition-all duration-300 hover:translate-x-2 hover:bg-black/[0.04]"
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rotate-45 rounded-[3px] bg-[#1d1d1f] transition-all duration-300 group-hover:rotate-[225deg] group-hover:scale-110 group-hover:bg-[#16a34a]"
                      aria-hidden="true"
                    />
                    <span className="font-semibold text-[#1d1d1f] transition-colors duration-300 group-hover:text-[#16a34a]">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-[#1d1d1f] font-normal">
              Tell us what you want. We'll handle everything else at the highest level. You'll only
              hear from us when we genuinely need you, like posing for a thumbnail.
            </p>
          </Reveal>
        </div>
        <div className="hidden lg:block">
          <CrewVisual />
        </div>
      </div>
    </section>
  );
}
